/**
 * `<caos-impersonation-banner>` — the persistent "you are logged in AS another user" strip,
 * reconstructing Dragon OS's ImpersonationBanner (UI-205) recolored to the `--caos-*` tokens.
 * (Catalog: Status & feedback › ImpersonationBanner — the persistent strip saying you are signed in as somebody else.)
 *
 * A full-width bar across the top of the shell shown ONLY while an admin is viewing the app as
 * another user, so the way back to yourself is reachable from every page. It paints the brand
 * ACCENT (Dragon red, `--caos-color-accent`) with white ink, a mono uppercase micro-label voice,
 * an Eye glyph, and an "End Login As" exit — matching Dragon's `var(--red,#C91313)` strip:
 *
 *   [👁] VIEWING <PLATFORM> AS <target (role)>  signed in as <real>            [✕ End Login As]
 *
 * Presentational + event-only (same contract as `<caos-topbar>`): it holds NO session logic.
 * With no `target-label` the whole element is hidden (renders nothing). The exit control emits a
 * composed `impersonation-end` the host binds to the identity-override runtime.
 *
 * Attributes (all optional strings): `target-label` (the user being viewed as; presence drives
 * visibility), `real-label` (the admin's own name), `product` (the platform name in the copy,
 * default "the app"). Styling seams: `::part(banner)`, `::part(exit)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { below } from '../breakpoints.js';
import { drawIcons } from './icons.js';

/* CAOS-602 — the marks come from the one vocabulary now. They used to be SVG SOURCE in the
   markup below, which put a second copy of two glyphs in this file and put artwork through
   `innerHTML` — the one construction route the content-security policy exists to rule out. The
   template leaves an empty `data-icon` holder and `drawIcons` fills it through the DOM. */

const sheet = makeSheet(`
  :host { display: block; }
  :host([hidden]) { display: none !important; }

  /* Dragon's strip: accent fill (red), white ink, mono uppercase micro-label, a hairline under.
     z-index sits above the frosted top bar so it is never occluded. */
  .banner {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 14px;
    /* CAOS-1551 — accent-fill under white text: white on accent is 3.60:1, below AA. */
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-family: var(--caos-font-mono);
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    /* CAOS-652 — the hairline, the chip's border and its focus ring are the banner's own ink at a
       reduced strength, so a brand that changes the ink changes them with it. */
    border-bottom: 1px solid color-mix(in srgb, var(--caos-color-accent-contrast) 18%, transparent);
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-banner);
  }

  .icon { flex-shrink: 0; width: 14px; height: 14px; display: inline-flex; }
  .icon svg { width: 100%; height: 100%; display: block; }

  /* CAOS-607 — the remaining time, between the message and the way out. Tabular figures so the
     digits do not shuffle sideways on every tick, and its own chip shading so it reads as a fact
     about the session rather than as another sentence in the message. */
  .left {
    flex-shrink: 0;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    padding: 2px 8px;
    border-radius: var(--caos-radius-sm);
    background: rgba(0, 0, 0, 0.12);
  }
  .left[hidden] { display: none; }

  .msg { flex: 1 1 auto; min-width: 0; }
  .msg .target { font-weight: 700; }
  /* the "signed in as …" tail drops the uppercase/mono emphasis (Dragon's lighter run). */
  .msg .real {
    opacity: 0.78;
    margin-left: 10px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0.04em;
  }

  .exit {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    /* CAOS-1551 — a DARKENING veil, not a lightening one. White 16% over the banner fill
       composites to a ground its own white label reads only 3.86:1 against (3.73:1 under the
       Atlantis brand), so this chip failed AA even once the banner behind it was fixed — the only
       site in this sweep that had to be COMPOSITED to be seen at all, because the declared colour
       says nothing about what a reader sees. Black 12% goes the other way: 6.30:1 and 6.07:1, and
       the chip still separates from the bar because it is darker rather than lighter.
       The veil stays black: it is a shade laid over the brand's own fill, not a colour of its own. */
    background: rgba(0, 0, 0, 0.12);
    color: var(--caos-color-accent-contrast);
    border: 1px solid color-mix(in srgb, var(--caos-color-accent-contrast) 40%, transparent);
    border-radius: var(--caos-radius-sm);
    padding: 3px 10px;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
  }
  /* Hover darkens for the same reason the rest state does — lightening it composited to
     3.22:1, worse than the state it hovers out of. */
  .exit:hover { background: rgba(0, 0, 0, 0.22); }
  .exit:focus-visible { outline: none; box-shadow: 0 0 0 2px color-mix(in srgb, var(--caos-color-accent-contrast) 70%, transparent); }
  .exit .exit-mark { display: inline-flex; }
  .exit svg { width: 12px; height: 12px; display: block; }

  @media ${below('medium')} {
    .banner { font-size: 11px; gap: 8px; padding: 6px 10px; }
    .msg .real { display: block; margin-left: 0; margin-top: 1px; }
  }
`);

export class CaosImpersonationBanner extends CaosElement {
  static get observedAttributes(): string[] {
    return ['target-label', 'real-label', 'product', 'expires-at'];
  }

  private els!: {
    banner: HTMLElement;
    icon: HTMLElement;
    msg: HTMLElement;
    left: HTMLElement;
    exit: HTMLButtonElement;
  };

  /** The countdown tick. Held so it can be stopped — see `disconnectedCallback`. */
  private ticker: ReturnType<typeof setInterval> | undefined;

  constructor() {
    super(sheet);
    const banner = document.createElement('div');
    banner.className = 'banner';
    banner.setAttribute('part', 'banner');
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.innerHTML = `
      <span class="icon" data-icon="eye"></span>
      <span class="msg"></span>
      <span class="left" part="remaining" hidden></span>
      <button type="button" class="exit" part="exit"><span class="exit-mark" data-icon="close"></span><span>End Login As</span></button>
    `;
    drawIcons(banner);
    this.root.append(banner);

    this.els = {
      banner,
      icon: banner.querySelector('.icon')!,
      msg: banner.querySelector('.msg')!,
      left: banner.querySelector('.left')!,
      exit: banner.querySelector('.exit')!,
    };

    this.els.exit.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('impersonation-end', { bubbles: true, composed: true }));
    });
  }

  connectedCallback(): void {
    this.sync();
    // Half a minute, not a second. The strip states how much time is left, not the current time,
    // and the difference between "12 MIN LEFT" and the truth is never more than the tick — while a
    // per-second timer would repaint a bar that is on screen for the whole session, permanently,
    // for a number that changes meaningfully about twice an hour.
    this.ticker ??= setInterval(() => this.paintRemaining(), 30_000);
  }

  /**
   * Stop the tick when the strip leaves the document. A banner that is no longer shown but is
   * still waking the page every half minute is the classic leak for a component like this one: it
   * is mounted for the length of a session, and a shell that re-renders its frame would otherwise
   * accumulate one live timer per render with nothing pointing at the old ones.
   */
  disconnectedCallback(): void {
    if (this.ticker !== undefined) {
      clearInterval(this.ticker);
      this.ticker = undefined;
    }
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /**
   * CAOS-607 — how long is left, in the coarsest honest unit.
   *
   * The cap is the reason an impersonated session is safe to grant, and a cap nobody can see is one
   * that ends as a surprise in the middle of an edit. Rounded UP to the minute above a minute, so
   * the strip never says "1 MIN LEFT" for fifty-nine seconds and then stops; switched to seconds in
   * the last minute, which is the only stretch where a person actually needs the precision.
   *
   * No `expires-at`, an unparseable one, or a deadline already passed → nothing is drawn. There is
   * no fallback guess: a countdown is a promise about time, and the wrong number is worse here than
   * no number, because somebody would plan around it.
   */
  private paintRemaining(): void {
    const raw = (this.getAttribute('expires-at') ?? '').trim();
    const at = raw ? Date.parse(raw) : Number.NaN;
    const ms = Number.isFinite(at) ? at - Date.now() : Number.NaN;
    if (!Number.isFinite(ms) || ms <= 0) {
      this.els.left.hidden = true;
      this.els.left.textContent = '';
      return;
    }
    const seconds = Math.ceil(ms / 1000);
    this.els.left.textContent =
      seconds < 60 ? `${seconds} sec left` : `${Math.ceil(seconds / 60)} min left`;
    this.els.left.hidden = false;
  }

  private sync(): void {
    const target = (this.getAttribute('target-label') ?? '').trim();
    // No target ⇒ no active impersonation ⇒ render nothing (Dragon's `if (!active) return null`).
    if (!target) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.paintRemaining();

    const real = (this.getAttribute('real-label') ?? '').trim();
    const product = (this.getAttribute('product') ?? '').trim() || 'the app';

    // Copy mirrors Dragon: "Viewing <product> as <target>  signed in as <real>".
    this.els.msg.replaceChildren();
    this.els.msg.append(document.createTextNode(`Viewing ${product} as `));
    const strong = document.createElement('span');
    strong.className = 'target';
    strong.textContent = target;
    this.els.msg.append(strong);
    if (real) {
      const tail = document.createElement('span');
      tail.className = 'real';
      tail.textContent = `signed in as ${real}`;
      this.els.msg.append(tail);
    }
  }
}

define('caos-impersonation-banner', CaosImpersonationBanner);

declare global {
  interface HTMLElementTagNameMap {
    'caos-impersonation-banner': CaosImpersonationBanner;
  }
}
